import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  EventEmitter,
  Output,
  Inject,
} from '@angular/core';
import { Location } from '@angular/common';
import { of, Subject, Subscription, timer } from 'rxjs';
import {
  takeUntil,
  startWith,
  pairwise,
  map,
  switchMap,
  catchError,
} from 'rxjs/operators';
import { ReadingGroup } from 'shared/models';

import { Address, BookType } from '@bukio/viewer';

import {
  VIEWER_ENVIRONMENT,
  ViewerModuleEnvironment,
} from '../../viewer.module';

import {
  NotesAPIService,
  NotificationsAPIService,
  ReadingGroupMembersAPIService,
  ReadingGroupsAPIService,
  SharedAuthService,
  SharedUserService,
  ViewerHistoryAPIService,
} from 'shared/services';
import { NotesDialogComponent, NotificationDialogComponent } from 'user';
import {
  AlertService,
  DialogService,
  MenuPositionBottomLeft,
  ToastService,
} from 'shared/ui';

import { EventBusService } from '../../services/event-bus.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { PageVisibilityStateService } from '../../services/page-visibility-state.service';
import { UIStateService } from '../../services/ui-state.service';
import { PDFDrawingService } from '../../services/pdf-drawing.service';
import { BookFeaturesStoreService } from '../../services/book-features-store.service';
import { AnalyticsService } from '../../services/analytics.service';

import { Book } from '../../models/book.model';

import { ThumbnailDialogComponent } from '../../dialogs/thumbnail-dialog/thumbnail-dialog.component';
import { SearchDialogComponent } from '../../dialogs/search-dialog/search-dialog.component';
import { ReadingGroupMembersDialogComponent } from '../../dialogs/reading-group-members-dialog/reading-group-members-dialog.component';
import { HelpDialogComponent } from '../../dialogs/help-dialog/help-dialog.component';
import { NewFeature1AlertComponent } from '../../dialogs/new-feature-1-alert/new-feature-1-alert.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { ReadingGroupDialogService } from 'reading-group';

export enum HeaderMenuItem {
  Notification = 'notification',
  Bookmark = 'bookmark',
  Back = 'back,',
  TOC = 'info',
  Thumbnail = 'thumbnail',
  Settings = 'setting',
  Help = 'help',
  ReadingMode = 'reading-mode',
  Search = 'search',
  GroupMembers = 'group-members',
  Inbox = 'inbox',
  OpenInNew = 'embed',
  DrawingMode = 'drawing-mode',
  GroupMemos = 'group-memos',
  MyRecord = 'my-record',
  BookHome = 'book-home',
}

const CHECK_INBOX_PERIOD = 3 * 60 * 1000;
const DRAWING_NOTI_LOCALSTORAGE_KEY = 'viewer-drawing-noti';
const DRAWING_NOTI_NEW_FEATURE_1 = 'viewer-drawing-noti-new-feature-1';

@Component({
  selector: 'viewer-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() drawingModeToggled = new EventEmitter<boolean>();
  @Output() noteUrlClicked = new EventEmitter<string>();

  private _unsubscriber = new Subject<void>();

  public readonly MenuItem = HeaderMenuItem;
  public readonly _embedded = self !== top;
  public readonly _isApp = this._environment.isApp;
  public _book?: Book;
  public _groups: ReadingGroup[] = []; // 나의책에서 접근 가능한 그룹책 목록
  public _address?: Address;

  public _groupMemberCount = 0;

  private _isUserLoggedIn = false;

  private _noteCheckSubscription?: Subscription;
  private _notificationCheckSubscription?: Subscription;
  public _hasNewNote = false;
  public _hasNewNotification = false;

  public _bookmarked = false;
  public _isDrawingMode = false;
  public _showDrawing = true;

  public _isCommentaryEditorMode = false;

  public _canSearchText = false;

  public _isRightPanelOpened = false;
  public _groupSelectMenuOpened = false;
  public _optionsPosition = MenuPositionBottomLeft;
  public _isSupplementViewer = false; // 부록보기 뷰어인지 여부
  public _isOwnedByUser = false; // 미리보기인지 여부
  public _currentGroupName?: string;
  public _isUnderline = false;

  public _isUserSubscribingLibrary = false;

  constructor(
    private _location: Location,
    private _userService: SharedUserService,
    private _eventBus: EventBusService,
    private _readingGroupsService: ReadingGroupsService,
    private _notesAPIService: NotesAPIService,
    private _notificationsAPIService: NotificationsAPIService,
    private _pageVisibilityStateService: PageVisibilityStateService,
    private _toastService: ToastService,
    private _analyticsService: AnalyticsService,
    private _dialogService: DialogService,
    private _alertService: AlertService,
    private _authService: SharedAuthService,
    private _uiStateService: UIStateService,
    @Optional() private _pdfDrawingService: PDFDrawingService,
    private _bookFeaturesStoreService: BookFeaturesStoreService,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private _readingGroupsAPIService: ReadingGroupsAPIService,
    private _annotationsService: AnnotationsV2Service,
    @Inject(VIEWER_ENVIRONMENT) private _environment: ViewerModuleEnvironment,
    private _readingGroupMembersAPIService: ReadingGroupMembersAPIService,
    private _readingGroupDialogService: ReadingGroupDialogService,
    private _viewerHistoryAPIService: ViewerHistoryAPIService
  ) {}

  ngOnInit(): void {
    this.activateRoute.queryParams.subscribe((params) => {
      this._isUnderline = params['isUnderline'] === 'true';
    });

    this._userService.user
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((user) => (this._isUserLoggedIn = !!user));

    this._uiStateService.isDrawingMode$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((isDrawingMode) => {
        this._isDrawingMode = isDrawingMode;
      });

    this._uiStateService.showDrawing$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((v) => (this._showDrawing = v));

    // right-panel 상태 구독 추가
    this._uiStateService.isRightPanelOpened$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((isOpened) => {
        this._isRightPanelOpened = isOpened;
      });

    this._pageVisibilityStateService.state$
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((state) => {
        if (state === 'hidden') {
          this._stopCheckInbox();
          this._stopCheckNotification();
        } else {
          const wasCheckingInbox =
            this._readingGroupsService.currentGroup && !this._hasNewNote;
          wasCheckingInbox && this._startCheckInbox();

          // Notification 체크는 항상 실행 (그룹 독서 여부와 무관)
          !this._isCheckingNotification && this._startCheckNotification();
        }
      });

    this._eventBus
      .on('ContentsComponent:addressChange')
      .pipe(
        takeUntil(this._unsubscriber),
        map((data) => data.address),
        startWith(undefined),
        pairwise()
      )
      .subscribe(([addr1, addr2]) => {
        this._address = addr2;
      });

    this._eventBus
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        this._isSupplementViewer = !!data.book.meta.is_subcontent;
        this._isOwnedByUser = !!data.book.isOwnedByUser;

        this._book = data.book;
        this._isCommentaryEditorMode = this._uiStateService.isCommentaryEditor;
        this._isUserSubscribingLibrary =
          data.book.permission.type === 'subscribe';

        this._readingGroupsAPIService
          .getJoined(0, 100, data.book.meta.bid)
          .pipe(takeUntil(this._unsubscriber))
          .subscribe((response) => {
            this._groups = response.items;
          });
      });

    this._readingGroupsService.currentGroup$.subscribe((group) => {
      this._groupMemberCount = group ? group.member_count : 0;
      this._currentGroupName = group?.name;

      if (group) {
        !this._isCheckingInbox && this._startCheckInbox(true);
      } else {
        this._stopCheckInbox();
      }

      // Notification 체크는 그룹 여부와 무관하게 항상 실행
      !this._isCheckingNotification && this._startCheckNotification(true);
    });

    this._eventBus
      .on('ContentsComponent:bookmarkStateChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        setTimeout(() => {
          this._bookmarked = data.isActive;
        }, 0);
      });

    this._bookFeaturesStoreService.features$.subscribe((features) => {
      this._canSearchText = features.text_search;
    });
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
    this._stopCheckInbox();
    this._stopCheckNotification();
  }

  get _isPDFMode(): boolean {
    return this._book?.meta.type === BookType.PDF;
  }

  get isGroupReading(): boolean {
    return !!this._readingGroupsService.currentGroup;
  }

  private _startCheckInbox(startImmediately?: boolean): void {
    this._stopCheckInbox();

    this._noteCheckSubscription = timer(
      startImmediately ? 0 : CHECK_INBOX_PERIOD,
      CHECK_INBOX_PERIOD
    )
      .pipe(
        switchMap(() => {
          return this._notesAPIService.checkInbox();
        })
      )
      .subscribe((newNote) => {
        this._hasNewNote = newNote;
        newNote && this._stopCheckInbox();
      });
  }

  private _stopCheckInbox(): void {
    this._noteCheckSubscription?.unsubscribe();
    this._noteCheckSubscription = undefined;
  }

  private _startCheckNotification(startImmediately?: boolean): void {
    this._stopCheckNotification();

    this._notificationCheckSubscription = timer(
      startImmediately ? 0 : CHECK_INBOX_PERIOD,
      CHECK_INBOX_PERIOD
    )
      .pipe(
        switchMap(() => {
          return this._notificationsAPIService.checkNew();
        })
      )
      .subscribe((hasNewNotification) => {
        this._hasNewNotification = hasNewNotification;
        hasNewNotification && this._stopCheckNotification();
      });
  }

  private _stopCheckNotification(): void {
    this._notificationCheckSubscription?.unsubscribe();
    this._notificationCheckSubscription = undefined;
  }

  private get _isCheckingInbox(): boolean {
    return !!this._noteCheckSubscription;
  }

  private get _isCheckingNotification(): boolean {
    return !!this._notificationCheckSubscription;
  }

  _onMenuItemClick(item: HeaderMenuItem): void {
    this._analyticsService.trackEvent('button', item);

    switch (item) {
      case HeaderMenuItem.Back:
        window.history.length > 1
          ? this._location.back()
          : this.router.navigate(['/']);
        break;
      case HeaderMenuItem.OpenInNew:
        this._openInNewTab();
        break;
      case HeaderMenuItem.Thumbnail:
        this._openThumbnailDialog();
        break;
      case HeaderMenuItem.Search:
        this._openSearchDialog();
        break;
      case HeaderMenuItem.TOC:
      case HeaderMenuItem.Settings:
      case HeaderMenuItem.GroupMemos:
        this._eventBus.fire('HeaderComponent:menuClick', { menu: item });
        break;
      case HeaderMenuItem.GroupMembers:
        this._openGroupMembersDialog();
        break;
      case HeaderMenuItem.Inbox:
        this._openInboxDialog();
        break;
      case HeaderMenuItem.Notification:
        this._openNotificationDialog();
        break;
      case HeaderMenuItem.Bookmark:
        this._onBookmarkButtonClick();
        break;
      case HeaderMenuItem.DrawingMode:
        this._onDrawingModeButtonClick();
        break;
      case HeaderMenuItem.Help:
        this._openHelpDialog();
        break;
      case HeaderMenuItem.MyRecord:
        this._eventBus.fire(
          'AnnotationSettingsFabComponent:anotationsButtonClick'
        );
        break;
      case HeaderMenuItem.ReadingMode:
        this._openReadingModeDialog();
        break;
      case HeaderMenuItem.BookHome:
        if (!this._book?.meta.parent_bid) {
          this.router.navigate(['@' + this._book?.meta.bid]);
        } else {
          this.router.navigate(['@' + this._book?.meta.parent_bid]);
        }
        break;
    }
  }

  private _openReadingModeDialog(): void {
    if (!this._book) {
      return;
    }

    const dialog = this._readingGroupDialogService.openReadingModeSelection(
      this._book.meta.bid,
      this._book.meta.title,
      this._book.meta.cover,
      this._book.meta.author
    );

    dialog.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      if (result.canceled) {
        return;
      }

      this._goToViewer(result.groupId);
    });
  }

  private _goToViewer(groupId?: number): void {
    if (!this._book) {
      return;
    }

    this._viewerHistoryAPIService
      .getLastReadPage(this._book.meta.bid)
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this._readingGroupsAPIService
          .getJoined(0, 100, this._book!.meta.bid)
          .pipe(takeUntil(this._unsubscriber))
          .subscribe((response) => {
            this._groups = response.items;
          });

        this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
          exited: null,
          entered: groupId ?? null,
        });

        this._eventBus.fire(
          'ReadingGroupMembersDialogComponent:memberDeselected'
        );

        this._annotationsService.setState({ groupId: groupId });
      });
  }

  private _onDrawingModeButtonClick(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    if (!this._isUserLoggedIn) {
      this._toastService
        .open('로그인 후 이용할 수 있습니다.', '로그인하기')
        .onAction()
        .subscribe(() => {
          this._authService.login(location.href);
        });
      return;
    }

    if (
      !this._pdfDrawingService ||
      !this._pdfDrawingService.isDeviceSupportsStylus
    ) {
      if (/BukHybridApp\//.test(navigator.userAgent)) {
        this._toastService.open(
          '펜 기능을 지원하는 기기에서만 사용 가능합니다.'
        );
      } else {
        this._toastService.open('필기 기능은 북이오 앱에서 이용해 주세요.');
      }

      return;
    }

    if (!this._uiStateService.canUseDrawingMode) {
      this._toastService.open(
        `필기를 불러오는데 실패하였습니다. 필기 기능 이용이 제한됩니다.`
      );
      return;
    }

    this._uiStateService.isDrawingMode = !this._uiStateService.isDrawingMode;
    // 필기모드를 키는 경우 header와 bottom이 닫히도록하는 로직
    this.drawingModeToggled.emit(this._uiStateService.isDrawingMode);

    if (!this._uiStateService.isDrawingMode) {
      return;
    }

    if (!this._showDrawing) {
      this._uiStateService.showDrawing = true;
    }

    if (localStorage.getItem(DRAWING_NOTI_LOCALSTORAGE_KEY) == null) {
      this._alertService
        .openConfirm(
          `앱을 삭제하면 필기 데이터도 삭제됩니다.<br/>앱 삭제는 신중히 진행해 주세요.`
        )
        .afterClosed()
        .subscribe(() => {
          localStorage.setItem(DRAWING_NOTI_LOCALSTORAGE_KEY, '');

          // 신규 사용자한테는 신규/변경 기능 노티 필요 없음
          localStorage.setItem(DRAWING_NOTI_NEW_FEATURE_1, '');
        });
    } else if (localStorage.getItem(DRAWING_NOTI_NEW_FEATURE_1) == null) {
      this._alertService
        .open(NewFeature1AlertComponent)
        .afterClosed()
        .subscribe(() => {
          localStorage.setItem(DRAWING_NOTI_NEW_FEATURE_1, '');
        });
    }
  }

  private _openHelpDialog(): void {
    if (!this._book) {
      return;
    }

    this._dialogService.open(HelpDialogComponent, {
      data: {
        isPDFBook: this._book.meta.type === BookType.PDF,
      },
    });
  }

  _onToggleDrawingVisibility(): void {
    this._uiStateService.showDrawing = !this._uiStateService.showDrawing;
  }

  private _openGroupMembersDialog(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    if (!this._book) {
      return;
    }

    // 그룹 멤버 팝업이 열리기 전에 header에 그룹원수 갱신을 위해 요청
    this._readingGroupMembersAPIService
      .getJoined(this._readingGroupsService.currentGroup!.id)
      .subscribe((response) => {
        this._groupMemberCount = response.length;
      });

    this._dialogService.open(ReadingGroupMembersDialogComponent, {
      data: {
        bid: this._book?.meta.bid,
      },
    });
  }

  private _openThumbnailDialog(): void {
    if (!this._book || !this._address) {
      return;
    }

    this._dialogService.open(ThumbnailDialogComponent, {
      data: {
        book: this._book,
        currentIid: this._address.iid || this._book.items[0].iid,
      },
    });
  }

  private _openSearchDialog(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    if (!this._book) {
      return;
    }

    this._dialogService.open(SearchDialogComponent, {
      data: {
        book: this._book,
      },
    });
  }

  private _openInboxDialog(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    this._hasNewNote = false;

    this._stopCheckInbox();

    this._dialogService
      .open(NotesDialogComponent, {
        data: {
          isViewer: true,
        },
      })
      .afterClosed()
      .subscribe((response) => {
        if (response) {
          // this.router.navigate([response.url]);
          this.noteUrlClicked.emit(response.url);
        } else {
          !this._isCheckingInbox && this._startCheckInbox();
        }
      });
  }

  private _openInNewTab(): void {
    if (
      this._address &&
      this._book?.canReadItem(this._address.iid || this._book.items[0].iid)
    ) {
      window.open(self.location.href);
    } else {
      const url = this._book?.permission.referer?.toString();
      url && window.open(url);
    }
  }

  private _onBookmarkButtonClick(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    if (!this._address || !this._book) {
      return;
    }

    const currentIid = this._address.iid || this._book.items[0].iid;

    if (!this._isUserLoggedIn) {
      this._toastService
        .open('로그인 후 이용할 수 있습니다.', '로그인하기')
        .onAction()
        .subscribe(() => {
          this._authService.login(location.href);
        });
    } else if (currentIid != null && !this._book.canReadItem(currentIid)) {
      this._toastService.openWarning(
        '해당 기능 사용을 위해서는 구매가 필요합니다.'
      );
    } else {
      this._bookmarked = !this._bookmarked;
      this._eventBus.fire('HeaderComponent:menuClick', {
        menu: HeaderMenuItem.Bookmark,
      });
    }
  }

  _onClickReadingGroup(group: ReadingGroup): void {
    // 선택한 그룹을 배열의 맨 앞으로 이동
    const groupIndex = this._groups.findIndex((g) => g.id === group.id);
    if (groupIndex > 0) {
      // 배열에서 제거하고 맨 앞에 추가
      this._groups.splice(groupIndex, 1);
      this._groups.unshift(group);
    }

    this._readingGroupsAPIService.touch(group.id).subscribe(() => {
      this._annotationsService.setState({
        groupId: group.id,
        memberId: undefined, // 그룹 변경 시 멤버 선택 해제
      });

      this._annotationsService.setState({
        groupId: group.id,
        memberId: undefined, // 그룹 변경 시 멤버 선택 해제
      });

      this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
        exited: null,
        entered: group.id ?? null,
      });

      this._eventBus.fire(
        'ReadingGroupMembersDialogComponent:memberDeselected'
      );

      this._annotationsService.setState({ groupId: group.id });

      // 그룹책 보기로 전환 토스트 메시지
      this._toastService.open(
        `'${group.name}' 그룹책 보기 모드로 변경되었습니다.`
      );
    });
  }

  _changeToMyBookViewer(): void {
    this._eventBus.fire('ReadingModeDialogComponent:groupChanged', {
      exited: null,
      entered: null,
    });

    this._eventBus.fire('ReadingGroupMembersDialogComponent:memberDeselected');

    this._annotationsService.setState({ groupId: undefined });

    // 나의책 보기로 전환 토스트 메시지
    this._toastService.open('나의책 보기 모드로 변경되었습니다.');
  }

  _openNotificationDialog(): void {
    if (!this._isOwnedByUser) {
      return;
    }

    this._hasNewNotification = false;

    this._stopCheckNotification();

    this._dialogService
      .open(NotificationDialogComponent, {
        responsiveBreakpoint: 768,
        data: {
          isApp: false,
          serviceOrigin: this._environment.serviceOrigin,
        },
      })
      .afterClosed()
      .subscribe(() => {
        !this._isCheckingNotification && this._startCheckNotification();
      });
  }
}
